function toDateSafe(value) {
  if (!value) return null;

  if (value instanceof Date) return value;
  if (typeof value.toDate === "function") return value.toDate();

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function normalizePeriodicidade(periodicidade) {
  const numero = Number(periodicidade);
  return Number.isFinite(numero) && numero > 0 ? numero : 4;
}

export function getPeriodoFiltro(periodicidade, ano, cicloNumero) {
  const ciclosPorAno = normalizePeriodicidade(periodicidade);
  const indiceCiclo = Math.max(Number(cicloNumero) || 1, 1);
  const mesesPorCiclo = 12 / ciclosPorAno;
  const mesInicio = Math.floor((indiceCiclo - 1) * mesesPorCiclo);

  const inicio = new Date(ano, mesInicio, 1, 0, 0, 0, 0);
  const fim = new Date(ano, mesInicio + mesesPorCiclo, 0, 23, 59, 59, 999);

  return { inicio, fim };
}

export function formatarRotuloCiclo(periodicidade, ano, cicloNumero) {
  const ciclosPorAno = normalizePeriodicidade(periodicidade);
  const indiceCiclo = Number(cicloNumero) || 1;

  if (ciclosPorAno === 12) {
    const { inicio } = getPeriodoFiltro(ciclosPorAno, ano, indiceCiclo);
    return `${inicio.toLocaleString("pt-BR", { month: "long" })}/${ano}`;
  }

  if (ciclosPorAno === 4) return `${ano} - ${indiceCiclo}o trimestre`;
  if (ciclosPorAno === 2) return `${ano} - ${indiceCiclo}o semestre`;
  if (ciclosPorAno === 1) return `${ano}`;

  return `${ano} - ciclo ${indiceCiclo}`;
}

export function formatarPeriodoCiclo(inicio, fim) {
  const dataInicio = toDateSafe(inicio);
  const dataFim = toDateSafe(fim);

  if (!dataInicio || !dataFim) return "-";

  return `${dataInicio.toLocaleDateString("pt-BR")} a ${dataFim.toLocaleDateString("pt-BR")}`;
}

export function formatarPeriodoCicloCurto(inicio, fim) {
  const dataInicio = toDateSafe(inicio);
  const dataFim = toDateSafe(fim);

  if (!dataInicio || !dataFim) return "-";

  const inicioFormatado = dataInicio.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  });
  const fimFormatado = dataFim.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  });

  return `${inicioFormatado}-${fimFormatado}`;
}

export function getCycleMeta(data = new Date(), periodicidade = 4) {
  const dataBase = toDateSafe(data) || new Date();
  const ciclosPorAno = normalizePeriodicidade(periodicidade);
  const mesesPorCiclo = 12 / ciclosPorAno;
  const cicloNumero = Math.floor(dataBase.getMonth() / mesesPorCiclo) + 1;
  const ano = dataBase.getFullYear();
  const { inicio, fim } = getPeriodoFiltro(ciclosPorAno, ano, cicloNumero);

  return {
    ano,
    cicloNumero,
    periodicidade: ciclosPorAno,
    chave: `${ano}-C${cicloNumero}`,
    rotulo: formatarRotuloCiclo(ciclosPorAno, ano, cicloNumero),
    inicio,
    fim,
  };
}

export function parseCycleIdentifier(ciclo) {
  if (!ciclo || typeof ciclo !== "string") return null;

  const match = ciclo.trim().match(/^(\d{4})-[A-Z]+(\d+)$/i);
  if (!match) return null;

  return {
    ano: Number(match[1]),
    cicloNumero: Number(match[2]),
  };
}

export function getCycleMetaFromResposta({
  createdAt,
  ciclo,
  ano,
  cicloNumero,
  periodicidade,
}) {
  const ciclosPorAno = normalizePeriodicidade(periodicidade);
  const parseado = parseCycleIdentifier(ciclo);
  const anoBase = Number(ano) || parseado?.ano;
  const cicloBase = Number(cicloNumero) || parseado?.cicloNumero;

  if (anoBase && cicloBase) {
    const { inicio, fim } = getPeriodoFiltro(ciclosPorAno, anoBase, cicloBase);
    return {
      ano: anoBase,
      cicloNumero: cicloBase,
      periodicidade: ciclosPorAno,
      chave: `${anoBase}-C${cicloBase}`,
      rotulo: formatarRotuloCiclo(ciclosPorAno, anoBase, cicloBase),
      inicio,
      fim,
    };
  }

  return getCycleMeta(createdAt, ciclosPorAno);
}

export function isCycleConcluded(cycleMeta, periodicidade, dataReferencia = new Date()) {
  if (!cycleMeta?.ano || !cycleMeta?.cicloNumero) return false;

  const cicloAtual = getCycleMeta(dataReferencia, periodicidade ?? cycleMeta.periodicidade);

  if (cycleMeta.ano !== cicloAtual.ano) {
    return cycleMeta.ano < cicloAtual.ano;
  }

  return cycleMeta.cicloNumero < cicloAtual.cicloNumero;
}

export function isCurrentCycle(cycleMeta, periodicidade, dataReferencia = new Date()) {
  if (!cycleMeta?.ano || !cycleMeta?.cicloNumero) return false;

  const cicloAtual = getCycleMeta(dataReferencia, periodicidade ?? cycleMeta.periodicidade);

  return (
    cycleMeta.ano === cicloAtual.ano &&
    cycleMeta.cicloNumero === cicloAtual.cicloNumero
  );
}

export function toDateFromUnknown(value) {
  return toDateSafe(value);
}

export function isDateWithinCycle(value, cycleMeta) {
  const data = toDateSafe(value);
  if (!data || !cycleMeta?.inicio || !cycleMeta?.fim) return false;

  return data >= cycleMeta.inicio && data <= cycleMeta.fim;
}

export function isDateBeforeCycle(value, cycleMeta) {
  const data = toDateSafe(value);
  if (!data || !cycleMeta?.inicio) return false;

  return data < cycleMeta.inicio;
}
